import type { TreeOptionFieldType } from '@tools/types/commonFields.types.ts';

type CategoryValue = string | number;

interface RawCategoryNode {
  id?: CategoryValue | null;
  value?: CategoryValue | null;
  key?: CategoryValue | null;
  name?: string | null;
  title?: string | null;
  label?: string | null;
  parent_id?: CategoryValue | null;
  sort_order?: number | null;
  depth?: number | null;
  children?: RawCategoryNode[] | null;
}

export interface DashboardCategoryTreeOptions {
  allowMergedSelection?: boolean;
}

export type DashboardCategoryTreeNode = TreeOptionFieldType & {
  value: CategoryValue;
  key: CategoryValue;
  children: DashboardCategoryTreeNode[];
  selectable?: boolean;
  meta?: {
    categoryIds?: CategoryValue[];
    merged?: boolean;
    parentId?: CategoryValue | null;
    sortOrder?: number | null;
  };
};

const MERGED_VALUE_PREFIX = 'category-merge:';

const normalizeName = (value: unknown): string =>
  String(value ?? '').trim().toLocaleLowerCase();

const toCategoryValue = (node: RawCategoryNode): CategoryValue => {
  const value = node.id ?? node.value ?? node.key;
  return value ?? normalizeName(node.name ?? node.title ?? node.label);
};

const toLabel = (node: RawCategoryNode, value: CategoryValue): string =>
  String(node.name ?? node.title ?? node.label ?? `Category-${value}`);

const uniqueValues = (values: CategoryValue[]): CategoryValue[] => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = String(value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mergedValue = (parentKey: string, label: string, ids: CategoryValue[]): string =>
  `${MERGED_VALUE_PREFIX}${encodeURIComponent(parentKey)}:${encodeURIComponent(normalizeName(label))}:${ids.map(String).join(',')}`;

export const isMergedDashboardCategoryValue = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith(MERGED_VALUE_PREFIX);

const parseEmbeddedId = (value: string): CategoryValue => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && /^-?\d+$/.test(value) ? numeric : value;
};

export const getDashboardCategoryIdsFromNode = (node: DashboardCategoryTreeNode): CategoryValue[] => {
  if (node.meta?.categoryIds?.length) {
    return node.meta.categoryIds;
  }
  return [node.value];
};

const buildNodes = (
  nodes: RawCategoryNode[],
  parentKey: string,
  options: Required<DashboardCategoryTreeOptions>,
): DashboardCategoryTreeNode[] => {
  const groups = new Map<string, DashboardCategoryTreeNode[]>();
  const order: string[] = [];

  nodes.forEach((rawNode) => {
    const value = toCategoryValue(rawNode);
    const label = toLabel(rawNode, value);
    // TreeSelect conduce la selección por `value`; la clave debe conservar
    // exactamente el mismo tipo para que un id numérico no busque una key string.
    const key = value;
    const node: DashboardCategoryTreeNode = {
      title: label,
      label,
      value,
      key,
      children: buildNodes(rawNode.children ?? [], String(key), options),
      meta: {
        categoryIds: [value],
        parentId: rawNode.parent_id ?? null,
        sortOrder: rawNode.sort_order ?? null,
      },
    };
    const groupKey = `${parentKey}:${rawNode.depth ?? ''}:${normalizeName(label)}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
      order.push(groupKey);
    }
    groups.get(groupKey)!.push(node);
  });

  return order.map((groupKey) => {
    const group = groups.get(groupKey) ?? [];
    if (group.length <= 1) {
      return group[0];
    }

    const first = group[0];
    const ids = uniqueValues(group.flatMap(getDashboardCategoryIdsFromNode));
    const children = buildNodes(
      group.flatMap((node) => node.children),
      String(first.key),
      options,
    );
    const value = mergedValue(parentKey, String(first.label ?? first.title), ids);
    return {
      title: first.title,
      label: first.label,
      value,
      key: value,
      selectable: options.allowMergedSelection,
      children,
      meta: {
        categoryIds: ids,
        merged: true,
        parentId: first.meta?.parentId ?? null,
        sortOrder: first.meta?.sortOrder ?? null,
      },
    };
  });
};

export const normalizeDashboardCategoryTree = (
  nodes: RawCategoryNode[] | unknown,
  options: DashboardCategoryTreeOptions = {},
): DashboardCategoryTreeNode[] => {
  if (!Array.isArray(nodes)) {
    return [];
  }
  return buildNodes(nodes, 'root', {
    allowMergedSelection: options.allowMergedSelection ?? true,
  });
};

export const includeSelectedDashboardCategories = (
  nodes: DashboardCategoryTreeNode[],
  selectedCategories: RawCategoryNode[] | null | undefined,
): DashboardCategoryTreeNode[] => {
  if (!Array.isArray(selectedCategories) || selectedCategories.length === 0) {
    return nodes;
  }
  const existingValues = new Set<string>();
  const collectValues = (current: DashboardCategoryTreeNode[]) => {
    current.forEach((node) => {
      getDashboardCategoryIdsFromNode(node).forEach((value) => existingValues.add(String(value)));
      collectValues(node.children ?? []);
    });
  };
  collectValues(nodes);

  const missing = selectedCategories.filter((category) => {
    const value = toCategoryValue(category);
    return !existingValues.has(String(value));
  });
  if (missing.length === 0) {
    return nodes;
  }
  return [
    ...nodes,
    ...buildNodes(missing, 'selected', { allowMergedSelection: true }),
  ];
};

export const expandDashboardCategoryValue = (
  value: unknown,
  nodes: DashboardCategoryTreeNode[],
): CategoryValue[] => {
  const walk = (current: DashboardCategoryTreeNode[]): CategoryValue[] | null => {
    for (const node of current) {
      if (node.value === value) {
        return getDashboardCategoryIdsFromNode(node);
      }
      const childMatch = walk(node.children ?? []);
      if (childMatch) return childMatch;
    }
    return null;
  };
  const match = walk(nodes);
  if (match) {
    return match;
  }
  if (isMergedDashboardCategoryValue(value)) {
    const encodedIds = value.split(':').slice(3).join(':');
    return encodedIds.split(',').filter(Boolean).map(parseEmbeddedId);
  }
  return value === undefined || value === null ? [] : [value as CategoryValue];
};

export const expandDashboardCategoryValues = (
  value: unknown,
  nodes: DashboardCategoryTreeNode[] = [],
): CategoryValue[] => {
  const values = Array.isArray(value) ? value : [value];
  return uniqueValues(values.flatMap((entry) => expandDashboardCategoryValue(entry, nodes)));
};

export const normalizeCategoryIdsForSubmit = (
  value: unknown,
  nodes: DashboardCategoryTreeNode[],
): { ids: number[]; invalid: unknown[] } => {
  const expanded = expandDashboardCategoryValues(value, nodes);
  const ids: number[] = [];
  const invalid: unknown[] = [];
  expanded.forEach((entry) => {
    const numeric = typeof entry === 'number' ? entry : Number(entry);
    if (Number.isInteger(numeric) && numeric > 0) {
      if (!ids.includes(numeric)) ids.push(numeric);
    } else {
      invalid.push(entry);
    }
  });
  return { ids, invalid };
};

export const resolveDashboardCategoryLabels = (
  value: unknown,
  nodes: DashboardCategoryTreeNode[],
): string[] => {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  const labels: string[] = [];
  const visit = (current: DashboardCategoryTreeNode[]) => {
    current.forEach((node) => {
      const nodeIds = getDashboardCategoryIdsFromNode(node).map(String);
      const selected = values.some((entry) => String(entry) === String(node.value) || nodeIds.includes(String(entry)));
      if (selected) {
        const label = String(node.label ?? node.title ?? '').trim();
        if (label && !labels.includes(label)) labels.push(label);
      }
      visit(node.children ?? []);
    });
  };
  visit(nodes);
  return labels;
};
